/**
 * clubs + handicap contracts — owned by work package api-clubs-handicap.
 * Request/response schemas of the `clubs` and `handicap` API modules (CLAUDE.md §5).
 * Names are prefixed `Clubs*` (clubs module) and `Hcp*` (handicap module) to avoid barrel clashes.
 */
import { z } from 'zod';
import { FormatConfigSchema, HandicapConfigSchema } from '../config/scoring';
import { ClubSettingsSchema, SeasonConfigSchema } from '../config/misc';
import {
  ClubRole,
  FormatKind,
  Gender,
  HandicapRecordSource,
  MembershipStatus,
  ParticipantStatus,
  StartType,
  TenantStatus,
  TournamentKind,
  TournamentStatus,
} from '../enums';
import { SessionResponseSchema } from './auth';

const DateOnly = z.iso.date(); // 'yyyy-MM-dd'
const HHmm = z.string().regex(/^([01]\d|2[0-3]):[0-5]\d$/);

// =============================================================================================
// clubs
// =============================================================================================

/** Partial Club.settings for create/PATCH (merged over the stored settings, then defaults). */
export const ClubsSettingsPatchSchema = z.object({
  defaultFormat: FormatConfigSchema.optional(),
  /** null clears the default handicap config */
  defaultHandicap: HandicapConfigSchema.nullable().optional(),
  allowSelfEntry: z.boolean().optional(),
  memberCanCreateEvents: z.boolean().optional(),
  defaultCourseId: z.uuid().nullable().optional(),
});
export type ClubsSettingsPatch = z.infer<typeof ClubsSettingsPatchSchema>;

export const ClubsCreateClubRequestSchema = z.object({
  name: z.string().trim().min(2).max(120),
  description: z.string().trim().max(2000).nullable().optional(),
  /** must be a SHARED course (a new club owns no courses yet) */
  homeCourseId: z.uuid().nullable().optional(),
  settings: ClubsSettingsPatchSchema.optional(),
  timezone: z.string().min(1).max(64).optional(),
  defaultLocale: z.enum(['vi', 'en', 'ko', 'zh', 'ja']).optional(),
});
export type ClubsCreateClubRequest = z.infer<typeof ClubsCreateClubRequestSchema>;

export const ClubsUpdateClubRequestSchema = z.object({
  name: z.string().trim().min(2).max(120).optional(),
  description: z.string().trim().max(2000).nullable().optional(),
  logoAssetId: z.uuid().nullable().optional(),
  homeCourseId: z.uuid().nullable().optional(),
  settings: ClubsSettingsPatchSchema.optional(),
});
export type ClubsUpdateClubRequest = z.infer<typeof ClubsUpdateClubRequestSchema>;

export const ClubsClubSchema = z.object({
  id: z.uuid(),
  tenantId: z.uuid(),
  name: z.string(),
  description: z.string().nullable(),
  logoAssetId: z.uuid().nullable(),
  homeCourseId: z.uuid().nullable(),
  homeCourseName: z.string().nullable(),
  settings: ClubSettingsSchema,
  status: TenantStatus,
  memberCount: z.number().int(),
  /** caller's role (null = super admin without membership) */
  myRole: ClubRole.nullable(),
  myMembershipId: z.uuid().nullable(),
  createdAt: z.iso.datetime(),
});
export type ClubsClub = z.infer<typeof ClubsClubSchema>;

export const ClubsClubListResponseSchema = z.object({ items: z.array(ClubsClubSchema) });

export const ClubsClubParamsSchema = z.object({ clubId: z.uuid() });
export const ClubsMemberParamsSchema = z.object({ clubId: z.uuid(), membershipId: z.uuid() });

export const ClubsMemberSchema = z.object({
  id: z.uuid(),
  clubId: z.uuid(),
  playerProfileId: z.uuid(),
  userId: z.uuid().nullable(),
  /** the profile is linked to a login account */
  hasAccount: z.boolean(),
  fullName: z.string(),
  role: ClubRole,
  status: MembershipStatus,
  memberNo: z.string().nullable(),
  clubHandicap: z.number().nullable(),
  /** PinGo HI (unofficial) of the profile */
  handicapIndex: z.number().nullable(),
  gender: Gender.nullable(),
  /** club admins only (null for members) */
  phone: z.string().nullable(),
  email: z.string().nullable(),
  joinedAt: z.iso.datetime(),
});
export type ClubsMember = z.infer<typeof ClubsMemberSchema>;

export const ClubsMemberListQuerySchema = z.object({
  /** filter one status; default = ACTIVE + PENDING */
  status: MembershipStatus.optional(),
  q: z.string().trim().max(100).optional(),
  cursor: z.uuid().optional(),
  limit: z.coerce.number().int().min(1).max(500).default(200),
});

export const ClubsMemberListResponseSchema = z.object({
  items: z.array(ClubsMemberSchema),
  nextCursor: z.string().nullable(),
});

export const ClubsAddMemberRequestSchema = z.object({
  fullName: z.string().trim().min(1).max(120),
  phone: z.string().trim().min(6).max(32),
  email: z.email().max(200).nullable().optional(),
  /** declared PinGo HI for a NEW profile (seed until 3 acceptable scores exist) */
  handicapIndex: z.number().min(-10).max(54).nullable().optional(),
  role: ClubRole.default('CLUB_MEMBER'),
  memberNo: z.string().trim().max(32).nullable().optional(),
  gender: Gender.nullable().optional(),
  birthYear: z.int().min(1900).max(2100).nullable().optional(),
});
export type ClubsAddMemberRequest = z.infer<typeof ClubsAddMemberRequestSchema>;

export const ClubsUpdateMemberRequestSchema = z.object({
  role: ClubRole.optional(),
  status: MembershipStatus.optional(),
  memberNo: z.string().trim().max(32).nullable().optional(),
  /** club-level handicap override (null = use the PinGo HI); changing it requires `reason` */
  clubHandicap: z.number().min(-10).max(54).nullable().optional(),
  reason: z.string().trim().max(500).optional(),
});
export type ClubsUpdateMemberRequest = z.infer<typeof ClubsUpdateMemberRequestSchema>;

// ---- invites ---------------------------------------------------------------------------------

export const ClubsInviteState = z.enum(['ACTIVE', 'EXPIRED', 'REVOKED', 'EXHAUSTED']);
export type ClubsInviteState = z.infer<typeof ClubsInviteState>;

export const ClubsCreateInviteRequestSchema = z.object({
  role: ClubRole.default('CLUB_MEMBER'),
  /** null/omitted = unlimited */
  maxUses: z.int().min(1).max(10000).nullable().optional(),
  expiresAt: z.iso.datetime({ offset: true }).nullable().optional(),
});
export type ClubsCreateInviteRequest = z.infer<typeof ClubsCreateInviteRequestSchema>;

export const ClubsInviteSchema = z.object({
  id: z.uuid(),
  clubId: z.uuid(),
  token: z.string(),
  /** web join link `/join/:token` */
  url: z.string(),
  role: ClubRole,
  maxUses: z.number().int().nullable(),
  uses: z.number().int(),
  expiresAt: z.iso.datetime().nullable(),
  revokedAt: z.iso.datetime().nullable(),
  state: ClubsInviteState,
  createdAt: z.iso.datetime(),
});
export type ClubsInvite = z.infer<typeof ClubsInviteSchema>;

export const ClubsInviteListResponseSchema = z.object({ items: z.array(ClubsInviteSchema) });

export const ClubsInviteQrQuerySchema = z.object({
  format: z.enum(['png', 'svg']).default('png'),
  /** PNG width in px */
  size: z.coerce.number().int().min(128).max(2048).default(512),
});

export const ClubsInvitePreviewSchema = z.object({
  clubId: z.uuid(),
  clubName: z.string(),
  description: z.string().nullable(),
  logoAssetId: z.uuid().nullable(),
  role: ClubRole,
  state: ClubsInviteState,
  valid: z.boolean(),
  expiresAt: z.iso.datetime().nullable(),
  memberCount: z.number().int(),
  /** club home course name (null = none set); additive, older servers omit it */
  homeCourseName: z.string().nullable().optional(),
});
export type ClubsInvitePreview = z.infer<typeof ClubsInvitePreviewSchema>;

export const ClubsAcceptInviteRequestSchema = z.object({
  /** used only when the account has no player profile yet */
  fullName: z.string().trim().min(1).max(120).optional(),
});

export const ClubsAcceptInviteResponseSchema = z.object({
  clubId: z.uuid(),
  alreadyMember: z.boolean(),
  membership: ClubsMemberSchema,
});
export type ClubsAcceptInviteResponse = z.infer<typeof ClubsAcceptInviteResponseSchema>;

// ---- events (정기전) -------------------------------------------------------------------------

export const ClubsEventRoundInputSchema = z.object({
  date: DateOnly,
  /** default: event courseId */
  courseId: z.uuid().optional(),
  extraCourseIds: z.array(z.uuid()).max(5).optional(),
  holeCount: z.union([z.literal(9), z.literal(18)]).optional(),
  firstHole: z.union([z.literal(1), z.literal(10)]).optional(),
  startType: StartType.optional(),
  firstTeeTime: HHmm.nullable().optional(),
  intervalMinutes: z.int().min(1).max(60).optional(),
});

export const ClubsCreateEventRequestSchema = z.object({
  name: z.string().trim().min(2).max(160),
  description: z.string().trim().max(2000).nullable().optional(),
  startDate: DateOnly,
  /** default = startDate (or the last round date) */
  endDate: DateOnly.optional(),
  holesPerRound: z.union([z.literal(9), z.literal(18)]).default(18),
  /** default course: settings.defaultCourseId -> club home course. Owned or SHARED. */
  courseId: z.uuid().optional(),
  extraCourseIds: z.array(z.uuid()).max(5).optional(),
  firstTeeTime: HHmm.nullable().optional(),
  startType: StartType.optional(),
  /** default: one round on startDate */
  rounds: z.array(ClubsEventRoundInputSchema).min(1).max(8).optional(),
  /** raw ScoringConfig (validated server-side); default {format: settings.defaultFormat, handicap: settings.defaultHandicap} */
  scoringConfig: z.record(z.string(), z.unknown()).optional(),
  /** partial TournamentPolicies merged over defaults */
  policies: z.record(z.string(), z.unknown()).optional(),
  registration: z.record(z.string(), z.unknown()).optional(),
  /** shortcut for policies.allowSelfEntry (default settings.allowSelfEntry) */
  allowSelfEntry: z.boolean().optional(),
  seasonId: z.uuid().nullable().optional(),
  timezone: z.string().min(1).max(64).optional(),
  defaultLocale: z.enum(['vi', 'en', 'ko', 'zh', 'ja']).optional(),
});
export type ClubsCreateEventRequest = z.input<typeof ClubsCreateEventRequestSchema>;

export const ClubsEventSchema = z.object({
  id: z.uuid(),
  clubId: z.uuid(),
  seasonId: z.uuid().nullable(),
  kind: TournamentKind,
  name: z.string(),
  description: z.string().nullable(),
  status: TournamentStatus,
  startDate: DateOnly,
  endDate: DateOnly,
  holesPerRound: z.number().int(),
  formatKind: FormatKind,
  allowSelfEntry: z.boolean(),
  participantCount: z.number().int(),
  rounds: z.array(
    z.object({
      id: z.uuid(),
      roundNo: z.number().int(),
      date: DateOnly,
      courseId: z.uuid(),
      status: z.enum(['SCHEDULED', 'IN_PROGRESS', 'COMPLETED', 'LOCKED']),
    }),
  ),
  /** the caller's own participant row in this event (via their player profile) */
  myParticipant: z
    .object({
      id: z.uuid(),
      status: ParticipantStatus,
      bibNumber: z.number().int().nullable(),
    })
    .nullable(),
  /**
   * PUBLIC_VIEW link token for the leaderboard / results (`/t/:token`); null when the event hides
   * its public leaderboard. Additive: older servers omit it.
   */
  publicToken: z.string().nullable().optional(),
  createdAt: z.iso.datetime(),
});
export type ClubsEvent = z.infer<typeof ClubsEventSchema>;

export const ClubsEventListQuerySchema = z.object({
  status: TournamentStatus.optional(),
  seasonId: z.uuid().optional(),
});
export const ClubsEventListResponseSchema = z.object({ items: z.array(ClubsEventSchema) });

export const ClubsEventParamsSchema = z.object({ clubId: z.uuid(), tournamentId: z.uuid() });

export const ClubsAddEventParticipantsRequestSchema = z.object({
  membershipIds: z.array(z.uuid()).min(1).max(500),
});

export const ClubsAddEventParticipantsResponseSchema = z.object({
  added: z.array(
    z.object({
      membershipId: z.uuid(),
      participantId: z.uuid(),
      bibNumber: z.number().int().nullable(),
    }),
  ),
  skipped: z.array(
    z.object({
      membershipId: z.uuid(),
      reason: z.enum(['ALREADY_REGISTERED', 'INACTIVE_MEMBERSHIP']),
      participantId: z.uuid().nullable(),
    }),
  ),
});
export type ClubsAddEventParticipantsResponse = z.infer<
  typeof ClubsAddEventParticipantsResponseSchema
>;

// ---- self entry ------------------------------------------------------------------------------

export const ClubsSelfEntrySessionRequestSchema = z.object({
  deviceId: z.string().max(64).optional(),
});

export const ClubsSelfEntrySessionResponseSchema = SessionResponseSchema.extend({
  participantId: z.uuid(),
  tournamentId: z.uuid(),
});
export type ClubsSelfEntrySessionResponse = z.infer<typeof ClubsSelfEntrySessionResponseSchema>;

// ---- seasons ---------------------------------------------------------------------------------

export const ClubsSeasonParamsSchema = z.object({ seasonId: z.uuid() });

export const ClubsCreateSeasonRequestSchema = z.object({
  name: z.string().trim().min(1).max(120),
  startDate: DateOnly,
  endDate: DateOnly,
  config: SeasonConfigSchema.optional(),
});
export type ClubsCreateSeasonRequest = z.input<typeof ClubsCreateSeasonRequestSchema>;

export const ClubsUpdateSeasonRequestSchema = z.object({
  name: z.string().trim().min(1).max(120).optional(),
  startDate: DateOnly.optional(),
  endDate: DateOnly.optional(),
  config: SeasonConfigSchema.optional(),
});

export const ClubsSeasonSchema = z.object({
  id: z.uuid(),
  clubId: z.uuid(),
  name: z.string(),
  startDate: DateOnly,
  endDate: DateOnly,
  config: SeasonConfigSchema,
  eventCount: z.number().int(),
  createdAt: z.iso.datetime(),
});
export type ClubsSeason = z.infer<typeof ClubsSeasonSchema>;

export const ClubsSeasonListResponseSchema = z.object({ items: z.array(ClubsSeasonSchema) });

export const ClubsSeasonStandingRowSchema = z.object({
  playerProfileId: z.uuid(),
  fullName: z.string(),
  points: z.number(),
  events: z.number().int(),
  wins: z.number().int(),
  rank: z.number().int(),
  positionLabel: z.string(),
});

export const ClubsSeasonStandingsSchema = z.object({
  seasonId: z.uuid(),
  clubId: z.uuid(),
  name: z.string(),
  config: SeasonConfigSchema,
  /** null = never computed */
  computedAt: z.iso.datetime().nullable(),
  /** FINALIZED events counted */
  events: z.array(
    z.object({
      tournamentId: z.uuid(),
      name: z.string(),
      startDate: DateOnly,
      status: TournamentStatus,
      counted: z.boolean(),
    }),
  ),
  rows: z.array(ClubsSeasonStandingRowSchema),
});
export type ClubsSeasonStandings = z.infer<typeof ClubsSeasonStandingsSchema>;

export const ClubsSeasonMemberParamsSchema = z.object({
  seasonId: z.uuid(),
  profileId: z.uuid(),
});

/**
 * One member's season points, event by event (GET /seasons/:id/standings/:profileId), computed on
 * request from the FINALIZED events with the same rules as the cached standings: `counted` marks
 * the best `bestN` events whose points make up `points`; the others are dropped.
 */
export const ClubsSeasonBreakdownSchema = z.object({
  seasonId: z.uuid(),
  playerProfileId: z.uuid(),
  fullName: z.string(),
  /** null = every event counts */
  bestN: z.number().int().nullable(),
  /** sum of the counted events' points */
  points: z.number(),
  /** chronological (event startDate) */
  events: z.array(
    z.object({
      tournamentId: z.uuid(),
      name: z.string(),
      startDate: DateOnly,
      /** finishing position in the season view (null = WD/DQ/no position) */
      position: z.number().int().nullable(),
      positionLabel: z.string(),
      points: z.number(),
      counted: z.boolean(),
    }),
  ),
});
export type ClubsSeasonBreakdown = z.infer<typeof ClubsSeasonBreakdownSchema>;

// =============================================================================================
// handicap ("PinGo HI (unofficial)" — WHS method, not an authorised association index)
// =============================================================================================

/**
 * ACCEPTED: counted as an (18-hole equivalent) differential; PENDING_NINE: 9-hole score of a player
 * without an index, waiting for a second 9-hole score; COMBINED: 9-hole score merged into the next
 * 9-hole record (combinedWithId); EXCLUDED: excluded by an admin.
 */
export const HcpRecordStatus = z.enum(['ACCEPTED', 'PENDING_NINE', 'COMBINED', 'EXCLUDED']);
export type HcpRecordStatus = z.infer<typeof HcpRecordStatus>;

/** HandicapIndexSnapshot.detail */
export const HcpCalcDetailSchema = z.object({
  usedRecordIds: z.array(z.string()).default([]),
  adjustment: z.number().default(0),
  capApplied: z.enum(['NONE', 'SOFT', 'HARD']).default('NONE'),
  considered: z.number().int().default(0),
  lowHandicapIndex: z.number().nullable().default(null),
  /** record whose posting produced this index */
  triggerRecordId: z.string().nullable().default(null),
  /** declared starting index (club admin), not computed from scores */
  seed: z.boolean().default(false),
});
export type HcpCalcDetail = z.infer<typeof HcpCalcDetailSchema>;

export const HcpRecordSchema = z.object({
  id: z.uuid(),
  playerProfileId: z.uuid(),
  source: HandicapRecordSource,
  tournamentId: z.uuid().nullable(),
  tournamentName: z.string().nullable(),
  roundNo: z.number().int().nullable(),
  playedOn: DateOnly,
  playedAt: z.iso.datetime(),
  holes: z.number().int(),
  grossScore: z.number().int(),
  adjustedGrossScore: z.number().int(),
  courseRating: z.number(),
  slopeRating: z.number().int(),
  par: z.number().int(),
  pcc: z.number(),
  /** 18-hole (equivalent) differential; raw 9-hole differential while PENDING_NINE */
  differential: z.number(),
  esrAdjustment: z.number(),
  excluded: z.boolean(),
  note: z.string().nullable(),
  status: HcpRecordStatus,
  /** counted among the lowest differentials of the current index */
  used: z.boolean(),
  /** COMBINED: the record it was merged into */
  combinedWithId: z.uuid().nullable(),
  createdAt: z.iso.datetime(),
});
export type HcpRecord = z.infer<typeof HcpRecordSchema>;

export const HcpSnapshotSchema = z.object({
  id: z.uuid(),
  handicapIndex: z.number().nullable(),
  effectiveAt: z.iso.datetime(),
  detail: HcpCalcDetailSchema,
});
export type HcpSnapshot = z.infer<typeof HcpSnapshotSchema>;

export const HcpProfileParamsSchema = z.object({ profileId: z.uuid() });
export const HcpRecordParamsSchema = z.object({ recordId: z.uuid() });

export const HcpPlayerHandicapSchema = z.object({
  playerProfileId: z.uuid(),
  fullName: z.string(),
  /** current PinGo HI (unofficial); null = not established */
  handicapIndex: z.number().nullable(),
  /** lowest index of the 365 days before the latest score (caps), null if none */
  lowHandicapIndex: z.number().nullable(),
  updatedAt: z.iso.datetime().nullable(),
  /** always false: PinGo is not an authorised handicap association */
  official: z.literal(false),
  /** most recent 20 records (newest first) with used markers */
  recent: z.array(HcpRecordSchema),
  /** index history (newest first, max 100) */
  history: z.array(HcpSnapshotSchema),
  totalRecords: z.number().int(),
  acceptedRecords: z.number().int(),
  /**
   * How the current computed index came out: average of the `usedCount` lowest of the latest
   * `considered` differentials, plus `adjustment` (table adjustment and exceptional-score
   * reductions), then caps. null while no index is computed from scores (fewer than 3, or only a
   * declared starting index). Additive: older servers omit it.
   */
  calculation: z
    .object({
      considered: z.number().int(),
      usedCount: z.number().int(),
      /** used differentials, lowest first */
      usedDifferentials: z.array(z.number()),
      sum: z.number(),
      /** sum / usedCount, unrounded */
      average: z.number(),
      adjustment: z.number(),
      capApplied: z.enum(['NONE', 'SOFT', 'HARD']),
      index: z.number(),
    })
    .nullable()
    .optional(),
});
export type HcpPlayerHandicap = z.infer<typeof HcpPlayerHandicapSchema>;

export const HcpCreateRecordRequestSchema = z
  .object({
    playedOn: DateOnly,
    /** exact time for same-day ordering (default: now / end of playedOn) */
    playedAt: z.iso.datetime({ offset: true }).optional(),
    holes: z.union([z.literal(9), z.literal(18)]).default(18),
    /** treated as the adjusted gross score (player posts net-double-bogey adjusted strokes) */
    grossScore: z.int().min(18).max(250),
    /** either a tee (rating looked up by gender) ... */
    teeBoxId: z.uuid().optional(),
    gender: Gender.optional(),
    /** 9-hole scores on a tee: which nine (default FRONT) */
    nine: z.enum(['FRONT', 'BACK']).optional(),
    /** ... or explicit rating values (9-hole values for 9-hole scores) */
    courseRating: z.number().min(25).max(90).optional(),
    slopeRating: z.int().min(55).max(155).optional(),
    par: z.int().min(27).max(80).optional(),
    pcc: z.number().int().min(-1).max(3).default(0),
    note: z.string().trim().max(500).nullable().optional(),
  })
  .superRefine((v, ctx) => {
    const byTee = v.teeBoxId !== undefined;
    const byRating =
      v.courseRating !== undefined && v.slopeRating !== undefined && v.par !== undefined;
    if (!byTee && !byRating) {
      ctx.addIssue({
        code: 'custom',
        path: ['teeBoxId'],
        message: 'Provide teeBoxId + gender or courseRating + slopeRating + par',
      });
    }
    if (byTee && !v.gender) {
      ctx.addIssue({
        code: 'custom',
        path: ['gender'],
        message: 'gender is required with teeBoxId',
      });
    }
  });
export type HcpCreateRecordRequest = z.input<typeof HcpCreateRecordRequestSchema>;

export const HcpUpdateRecordRequestSchema = z
  .object({
    excluded: z.boolean().optional(),
    note: z.string().trim().max(500).nullable().optional(),
  })
  .refine((v) => v.excluded !== undefined || v.note !== undefined, {
    message: 'Nothing to update',
  });

export const HcpClubHandicapsResponseSchema = z.object({
  clubId: z.uuid(),
  items: z.array(
    z.object({
      membershipId: z.uuid(),
      playerProfileId: z.uuid(),
      fullName: z.string(),
      handicapIndex: z.number().nullable(),
      clubHandicap: z.number().nullable(),
      updatedAt: z.iso.datetime().nullable(),
      recordCount: z.number().int(),
      /** last 12 index values, oldest first */
      trend: z.array(
        z.object({ effectiveAt: z.iso.datetime(), handicapIndex: z.number().nullable() }),
      ),
    }),
  ),
});
export type HcpClubHandicaps = z.infer<typeof HcpClubHandicapsResponseSchema>;

export const HcpCourseHandicapQuerySchema = z.object({
  index: z.coerce.number().min(-10).max(54),
  teeBoxId: z.uuid(),
  gender: Gender.default('M'),
  holes: z.coerce
    .number()
    .int()
    .refine((h) => h === 9 || h === 18, 'holes must be 9 or 18')
    .default(18),
  nine: z.enum(['FRONT', 'BACK']).default('FRONT'),
  /** playing handicap allowance (default 0.95 individual stroke play) */
  allowance: z.coerce.number().min(0).max(1).default(0.95),
});

export const HcpCourseHandicapResponseSchema = z.object({
  teeBoxId: z.uuid(),
  teeName: z.string(),
  courseId: z.uuid(),
  courseName: z.string(),
  gender: Gender,
  holes: z.number().int(),
  handicapIndex: z.number(),
  courseRating: z.number(),
  slopeRating: z.number().int(),
  par: z.number().int(),
  courseHandicapExact: z.number(),
  courseHandicap: z.number().int(),
  allowance: z.number(),
  playingHandicap: z.number().int(),
});
export type HcpCourseHandicap = z.infer<typeof HcpCourseHandicapResponseSchema>;
